import { useEffect, useRef, useState } from 'react';
import { formatDate, FORMAT } from '../../../../../lib/date';
import CompleteStamp from './stamp';
import request from '../../../../../lib/request';

const CompleteDialog = ({
  completeDialogRef,
  closeDialog,
  isDialogOpen,
  workoutTime,
  closeWorkoutDialog,
  routine,
}) => {
  const bottomRef = useRef(null);
  const randomIndex = useRef(Math.floor(Math.random() * 3));

  const [dateTime, setDateTime] = useState('');
  const [duration, setDuration] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [loading, setLoading] = useState(false);
  const [emojiExpression, setEmojiExpression] = useState([]);
  const [note, setNote] = useState('');
  const [feeling, setFeeling] = useState([]);
  const [level, setLevel] = useState(1);

  useEffect(() => {
    const clickOutsideDialog = (e) => {
      if (
        completeDialogRef.current.contains(e.target) &&
        !bottomRef.current.contains(e.target)
      ) {
        handleCancel();
      }
    };

    document.addEventListener('click', clickOutsideDialog);

    return () => {
      document.removeEventListener('click', clickOutsideDialog);
    };
  }, []);

  useEffect(() => {
    if (isDialogOpen) {
      getEmojiExpression();

      const hours = Math.floor(workoutTime / 3600);
      const minutes = Math.floor((workoutTime % 3600) / 60);
      const seconds = Math.floor((workoutTime % 3600) % 60);
      setDuration({
        hours,
        minutes,
        seconds,
      });

      setDateTime(
        `${formatDate(new Date())}T${formatDate(new Date(), FORMAT.HOURMIN)}`
      );
    } else {
      setDuration({ hours: 0, minutes: 0, seconds: 0 });
    }
  }, [isDialogOpen]);

  const getEmojiExpression = async () => {
    try {
      const { result } = await request.get('/static/emoji-expression');
      setEmojiExpression(result);
    } catch (err) {
      console.log(err);
    }
  };

  const resetStates = () => {
    setDateTime('');
    setDuration({ hours: 0, minutes: 0, seconds: 0 });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const logDateTime = dateTime.split('T');
      const logDate = logDateTime[0];
      const logTime = logDateTime[1];
      const logs = [];

      const data = {
        workout: routine.title,
        logDate,
        logTime,
        note,
        feeling: feeling.join(','),
        type: 'PROGRAM',
        duration:
          duration.hours * 60 * 60 + duration.minutes * 60 + duration.seconds,
        logs,
        programRoutineId: routine.id,
        level,
        workoutAt: new Date(dateTime).toISOString(),
      };

      if (routine.cycle) {
        data.cycle = routine.cycle;
      }

      const { result } = await request.post(`/workout/record`, data);
      if (!result) {
        return alert('Fail to record');
      }

      resetStates();
      closeDialog();
      closeWorkoutDialog();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (
      window.confirm(
        'You can manually record your workout log on "Workout Tracker" section.'
      )
    ) {
      resetStates();
      closeDialog();
    }
  };

  const handleChangeNote = (e) => {
    setNote(e.target.value);
  };

  const handleSelectFeeling = (emoji) => {
    if (feeling.includes(emoji)) {
      setFeeling(feeling.filter((item) => item !== emoji));
    } else if (feeling.length < 3) {
      setFeeling([...feeling, emoji]);
    } else {
      alert('You selected 3 best feelings.');
    }
  };

  return (
    <dialog
      ref={completeDialogRef}
      className='w-full h-full max-w-lg max-h-full bg-transparent'
      onCancel={handleCancel}
    >
      <div
        ref={bottomRef}
        className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 pt-6 pb-3 flex flex-col h-[90%] overflow-y-auto scrollbar-hidden`}
      >
        <div className='flex items-center justify-between mb-4 text-white'>
          <h1 className='text-xl font-bold flex flex-wrap gap-1'>
            {routine?.title}
          </h1>
          <button
            onClick={handleCancel}
            className='text-2xl'
          >
            🅧
          </button>
        </div>
        {!loading && (
          <CompleteStamp
            workoutTitle={routine?.title}
            selectedDay={routine?.date}
            randomIndex={randomIndex.current}
          />
        )}
        <div className='space-y-2 mt-4'>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-white font-semibold'>Date</label>
            <input
              type='datetime-local'
              max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className='rounded-full px-2 py-1 bg-tertiary  text-white'
            />
          </div>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-white font-semibold'>Duration</label>
            <div className='text-white flex items-center gap-2'>
              {duration.hours > 0 && (
                <div className='flex items-center gap-2'>
                  <div className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-14 text-center'>
                    {duration.hours}
                  </div>
                  <span>h</span>
                </div>
              )}
              {duration.minutes > 0 && (
                <div className='flex items-center gap-2'>
                  <div className='rounded-full text-whitetext-center'>
                    {duration.minutes}
                  </div>
                  <span>mins</span>
                </div>
              )}
              {duration.seconds > 0 && (
                <div className='flex items-center gap-2'>
                  <div className='rounded-full text-white text-center'>
                    {duration.seconds}
                  </div>
                  <span>sec</span>
                </div>
              )}
            </div>
          </div>
          <div className='flex items-center justify-between gap-2 mb-4'>
            <label className='text-white font-semibold'>Level</label>
            <select
              name='level'
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className='rounded-full px-2 py-1 bg-tertiary  text-white text-center'
            >
              {Array.from({ length: 5 }, (_, i) => (
                <option
                  key={i}
                  value={i + 1}
                >
                  {i + 1}
                  {i + 1 === 1 ? ' (easy)' : i + 1 === 5 ? ' (hard)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className='mb-4'>
            <div className='text-white font-semibold'>
              <span className='mr-2'>How was today's workout?</span>
              <span className='text-xl space-x-1'>
                {Array.from({ length: 3 }, (_, i) => (
                  <span key={i}>{feeling[i] ? feeling[i] : '🫥'}</span>
                ))}
              </span>
            </div>
            <div className='text-xs text-white/50 mb-1'>
              Select 1-3 best feelings
            </div>
            <div className='flex items-center gap-4 py-2 overflow-x-scroll scrollbar-hidden mb-4'>
              {emojiExpression.map((item) => (
                <button
                  key={item.emoji}
                  className={`flex-shrink-0 size-[72px] flex flex-col items-center justify-center gap-1 rounded-xl p-1 ${
                    feeling.includes(item.emoji)
                      ? 'bg-secondary border-2 border-accent'
                      : 'bg-tertiary'
                  }`}
                  onClick={() => handleSelectFeeling(item.emoji)}
                >
                  <span className='text-xl'>{item.emoji}</span>
                  <span className='text-[10px] text-white/70 leading-none'>
                    {item.meaning}
                  </span>
                </button>
              ))}
            </div>
            <div className='flex flex-col gap-2'>
              <label className='text-white font-semibold'>Note</label>
              <textarea
                value={note}
                rows={2}
                onChange={handleChangeNote}
                className='rounded-xl px-3 py-2 bg-tertiary  text-white resize-none'
              />
            </div>
          </div>
        </div>
        <div className='mt-4'>
          <button
            className='bg-primary text-white py-3 rounded-full w-full'
            onClick={handleSubmit}
            disabled={loading}
          >
            Complete
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default CompleteDialog;
